import { get } from 'lodash';
/** Pass / zone / tilt views live on the limits API (v1), not REACT_APP_BASE_URL / data360. */
import axiosConfig from '../../../redux/WeldData/axiosConfig';
import {
  LIMITS_VIEW_REQUEST_TIMEOUT_MS,
  withLimitsViewSlot,
} from './limitsViewFetchQueue';
import type { LimitsRow, ParamValueTriple, WpsViolationRecord } from './wpsViolationModel';
import { isStatusFailed, violationsFromViewRecord } from './wpsViolationModel';

type LimitsViewResponse = { status: number; data?: unknown };

const passViewInFlight = new Map<string, Promise<LimitsViewResponse>>();

async function getLimitsView(url: string, priority = 1): Promise<LimitsViewResponse> {
  const running = passViewInFlight.get(url);
  if (running) return running;

  return withLimitsViewSlot(async () => {
    const already = passViewInFlight.get(url);
    if (already) return already;
    const request = axiosConfig
      .get(url, { timeout: LIMITS_VIEW_REQUEST_TIMEOUT_MS })
      .finally(() => {
        if (passViewInFlight.get(url) === request) passViewInFlight.delete(url);
      });
    passViewInFlight.set(url, request);
    return request;
  }, priority);
}

export interface LimitsViewFetchOptions {
  /** 0 jumps ahead of background batch loads. */
  priority?: number;
}

function handleNullValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Build a param triple; keep status-failed params even when all numeric values are null. */
export function createParamObject(
  low: unknown,
  avg: unknown,
  high: unknown,
  avgStatus?: unknown,
  lowStatus?: unknown,
  highStatus?: unknown,
): ParamValueTriple | null {
  const lowVal = handleNullValue(low);
  const avgVal = handleNullValue(avg);
  const highVal = handleNullValue(high);
  const hasFailedStatus =
    isStatusFailed(avgStatus) || isStatusFailed(lowStatus) || isStatusFailed(highStatus);
  if (lowVal === null && avgVal === null && highVal === null && !hasFailedStatus) {
    return null;
  }
  return {
    min: lowVal,
    avg: avgVal,
    max: highVal,
    avgStatus: avgStatus as ParamValueTriple['avgStatus'],
    lowStatus: lowStatus as ParamValueTriple['lowStatus'],
    highStatus: highStatus as ParamValueTriple['highStatus'],
  };
}

export interface PassViewRecord {
  id: string | number;
  name: string;
  jobNumber: string;
  /** Present when the limits view row includes weld_number (job-scoped fetches). */
  weldNumber?: string;
  welderId: string;
  status: string;
  startTime?: string;
  endTime?: string;
  torch?: string;
  bugType?: string;
  stationNumber?: string;
  weldTimeMinutes?: number | null;
  passStatusInstantaneous?: string | null;
  passStatusZone?: string | null;
  passAvgStatus?: string | null;
  params: Partial<Record<string, ParamValueTriple | null>>;
}

export interface ZoneViewRecord {
  id: string | number;
  zone: string;
  pass: string;
  jobNumber: string;
  weldNumber?: string;
  status: string;
  welderId?: string;
  params: Partial<Record<string, ParamValueTriple | null>>;
}

export interface TiltViewRecord {
  id: string | number;
  pass: string;
  jobNumber: string;
  weldNumber?: string;
  tiltStart: number | null;
  tiltEnd: number | null;
  status: string;
  welderId?: string;
  welduid_s_event_torch?: string | null;
  params: Partial<Record<string, ParamValueTriple | null>>;
}

export interface FetchWeldViewParams {
  project_id: number;
  project_name?: string;
  /** Required for drill-down loads. A job-wide call without this mixes welds and blocks the queue. */
  weld_number?: string;
  job_number?: string;
}

function viewWeldNumber(raw: Record<string, unknown>): string {
  const value = raw.weld_number ?? raw.weldNumber;
  return value == null ? '' : String(value).trim();
}

export async function fetchPassViewRecords(
  params: FetchWeldViewParams,
  options?: LimitsViewFetchOptions,
): Promise<PassViewRecord[]> {
  const { project_id, project_name, weld_number, job_number } = params;
  let url = `/view/pass?project_id=${project_id}`;
  if (project_name) url += `&project_name=${encodeURIComponent(project_name)}`;
  if (weld_number) url += `&weld_number=${encodeURIComponent(weld_number)}`;
  if (job_number) url += `&job_number=${encodeURIComponent(job_number)}`;

  const res = await getLimitsView(url, options?.priority ?? 1);
  if (res.status < 200 || res.status >= 300) return [];
  const passes = get(res, ['data', 'data', 'data'], []);
  if (!Array.isArray(passes)) return [];

  return passes.map((pass: Record<string, unknown>) => ({
    id: pass.id as string | number,
    name: String(pass.pass_name ?? '-'),
    jobNumber: String(pass.job_number ?? pass.weld_uid ?? ''),
    weldNumber: viewWeldNumber(pass),
    welderId: String(pass.welder_id ?? '—'),
    status: String(pass.pass_status_by_instantaneous ?? pass.pass_avg_status ?? ''),
    startTime: pass.s_time ? String(pass.s_time) : undefined,
    endTime: pass.c_time ? String(pass.c_time) : undefined,
    torch: String(pass.torch ?? ''),
    bugType: pass.bug_type != null && pass.bug_type !== '' ? String(pass.bug_type) : undefined,
    stationNumber: pass.station_number != null ? String(pass.station_number) : undefined,
    weldTimeMinutes: handleNullValue(pass.weld_time),
    passStatusInstantaneous: pass.pass_status_by_instantaneous != null
      ? String(pass.pass_status_by_instantaneous)
      : null,
    passStatusZone: pass.pass_staus_by_zone != null ? String(pass.pass_staus_by_zone) : null,
    passAvgStatus: pass.pass_avg_status != null ? String(pass.pass_avg_status) : null,
    params: {
      volt: createParamObject(
        pass.volts_low, pass.volts_avg, pass.volts_high,
        pass.volts_avg_status, pass.volts_low_status, pass.volts_high_status,
      ),
      current: createParamObject(
        pass.current_low, pass.current_avg, pass.current_high,
        pass.current_avg_status, pass.current_low_status, pass.current_high_status,
      ),
      travelSpeed: createParamObject(
        pass.travel_speed_low, pass.travel_speed_avg, pass.travel_speed_high,
        pass.travel_speed_avg_status, pass.travel_speed_low_status, pass.travel_speed_high_status,
      ),
      heat: createParamObject(
        pass.heat_low, pass.heat_avg, pass.heat_high,
        pass.heat_avg_status, pass.heat_low_status, pass.heat_high_status,
      ),
      trueEnergy: createParamObject(
        pass.true_energy_low, pass.true_energy_avg, pass.true_energy_high,
        pass.true_energy_avg_status, pass.true_energy_low_status, pass.true_energy_high_status,
      ),
      wireSpeed: createParamObject(
        pass.wire_speed_low, pass.wire_speed_avg, pass.wire_speed_high,
        pass.wire_speed_avg_status, pass.wire_speed_low_status, pass.wire_speed_high_status,
      ),
      oscillationWidth: createParamObject(
        pass.oscillation_width_low, pass.oscillation_width_avg, pass.oscillation_width_high,
        pass.oscillation_width_avg_status, pass.oscillation_width_low_status, pass.oscillation_width_high_status,
      ),
    },
  }));
}

export async function fetchZoneViewRecords(
  params: FetchWeldViewParams,
  options?: LimitsViewFetchOptions,
): Promise<ZoneViewRecord[]> {
  const { project_id, project_name, weld_number, job_number } = params;
  let url = `/view/zone?project_id=${project_id}`;
  if (project_name) url += `&project_name=${encodeURIComponent(project_name)}`;
  if (weld_number) url += `&weld_number=${encodeURIComponent(weld_number)}`;
  if (job_number) url += `&job_number=${encodeURIComponent(job_number)}`;

  const res = await getLimitsView(url, options?.priority ?? 1);
  if (res.status < 200 || res.status >= 300) return [];
  const zones = get(res, ['data', 'data', 'data'], []);
  if (!Array.isArray(zones)) return [];

  return zones.map((zone: Record<string, unknown>) => ({
    id: zone.id as string | number,
    zone: String(zone.zone_name ?? zone.zone ?? '-'),
    pass: String(zone.pass_name ?? '-'),
    jobNumber: String(zone.job_number ?? ''),
    weldNumber: viewWeldNumber(zone),
    status: String(zone.zone_status_by_instantaneuous ?? zone.avg_zone_status ?? ''),
    welderId: String((zone.pass_view as Record<string, unknown> | undefined)?.welder_id ?? '—'),
    params: {
      volt: createParamObject(zone.volts_low, zone.volts_avg, zone.volts_high, zone.volts_avg_status, zone.volts_low_status, zone.volts_high_status),
      current: createParamObject(zone.current_low, zone.current_avg, zone.current_high, zone.current_avg_status, zone.current_low_status, zone.current_high_status),
      travelSpeed: createParamObject(zone.travel_speed_low, zone.travel_speed_avg, zone.travel_speed_high, zone.travel_speed_avg_status, zone.travel_speed_low_status, zone.travel_speed_high_status),
      heat: createParamObject(zone.heat_low, zone.heat_avg, zone.heat_high, zone.heat_avg_status, zone.heat_low_status, zone.heat_high_status),
      trueEnergy: createParamObject(zone.true_energy_low, zone.true_energy_avg, zone.true_energy_high, zone.true_energy_avg_status, zone.true_energy_low_status, zone.true_energy_high_status),
      wireSpeed: createParamObject(zone.wire_speed_low, zone.wire_speed_avg, zone.wire_speed_high, zone.wire_speed_avg_status, zone.wire_speed_low_status, zone.wire_speed_high_status),
      oscillationWidth: createParamObject(zone.oscillation_width_low, zone.oscillation_width_avg, zone.oscillation_width_high, zone.oscillation_width_avg_status, zone.oscillation_width_low_status, zone.oscillation_width_high_status),
    },
  }));
}

export async function fetchTiltViewRecords(
  params: FetchWeldViewParams,
  options?: LimitsViewFetchOptions,
): Promise<TiltViewRecord[]> {
  const { project_id, project_name, weld_number, job_number } = params;
  let url = `/view/tilt?project_id=${project_id}`;
  if (project_name) url += `&project_name=${encodeURIComponent(project_name)}`;
  if (weld_number) url += `&weld_number=${encodeURIComponent(weld_number)}`;
  if (job_number) url += `&job_number=${encodeURIComponent(job_number)}`;

  const res = await getLimitsView(url, options?.priority ?? 1);
  if (res.status < 200 || res.status >= 300) return [];
  const tilts = get(res, ['data', 'data', 'data'], []);
  if (!Array.isArray(tilts)) return [];

  return tilts.map((tilt: Record<string, unknown>) => ({
    id: tilt.id as string | number,
    pass: String(tilt.pass_name ?? '-'),
    jobNumber: String(tilt.job_number ?? ''),
    weldNumber: viewWeldNumber(tilt),
    tiltStart: handleNullValue(tilt.tilt_start),
    tiltEnd: handleNullValue(tilt.tilt_end),
    status: String(tilt.avg_tilt_status ?? tilt.avg_staus_by_instantaneous ?? ''),
    welderId: String(tilt.welder_id ?? '—'),
    welduid_s_event_torch: tilt.welduid_s_event_torch as string | null,
    params: {
      volt: createParamObject(tilt.volts_low, tilt.volts_avg, tilt.volts_high, tilt.volts_avg_status, tilt.volts_low_status, tilt.volts_high_status),
      current: createParamObject(tilt.current_low, tilt.current_avg, tilt.current_high, tilt.current_avg_status, tilt.current_low_status, tilt.current_high_status),
      travelSpeed: createParamObject(tilt.travel_speed_low, tilt.travel_speed_avg, tilt.travel_speed_high, tilt.travel_speed_avg_status, tilt.travel_speed_low_status, tilt.travel_speed_high_status),
      heat: createParamObject(tilt.heat_low, tilt.heat_avg, tilt.heat_high, tilt.heat_avg_status, tilt.heat_low_status, tilt.heat_high_status),
      trueEnergy: createParamObject(tilt.true_energy_low, tilt.true_energy_avg, tilt.true_energy_high, tilt.true_energy_avg_status, tilt.true_energy_low_status, tilt.true_energy_high_status),
      wireSpeed: createParamObject(tilt.wire_speed_low, tilt.wire_speed_avg, tilt.wire_speed_high, tilt.wire_speed_avg_status, tilt.wire_speed_low_status, tilt.wire_speed_high_status),
      oscillationWidth: createParamObject(tilt.oscillation_width_low, tilt.oscillation_width_avg, tilt.oscillation_width_high, tilt.oscillation_width_avg_status, tilt.oscillation_width_low_status, tilt.oscillation_width_high_status),
    },
  }));
}

export interface ProjectLimitsData {
  allDataSet: LimitsRow[];
  projectData?: Record<string, unknown>;
}

export async function fetchProjectLimitsApi(projectId: number): Promise<ProjectLimitsData> {
  try {
    const res = await axiosConfig.get(`/limits/get-limits?project_id=${projectId}`, {
      timeout: 30_000,
    });
    if (res.status >= 200 && res.status < 300) {
      const data = get(res, ['data'], {}) as {
        success?: number;
        data?: { allDataSet?: LimitsRow[]; projectData?: Record<string, unknown> };
      };
      if (data.success === 1 && data.data) {
        return {
          allDataSet: Array.isArray(data.data.allDataSet) ? data.data.allDataSet : [],
          projectData: data.data.projectData,
        };
      }
    }
  } catch {
    // Limits are optional for pass summary; violations may show status-only rows.
  }
  return { allDataSet: [] };
}

export interface TlogDetailRecord {
  tilt: number | null;
  time: number | null;
  volts: number | null;
  current: number | null;
  travelSpeed: number | null;
  heat: number | null;
}

export async function fetchTiltViewDetailsApi(
  projectId: number,
  projectName: string | undefined,
  welduid_s_event_torch: string,
  tiltStart: number,
  tiltEnd: number,
  viewType = 'Tilt',
  torchType = 'Lead',
): Promise<TlogDetailRecord[]> {
  const encodedProjectId = encodeURIComponent(String(projectId));
  const encodedProjectName = projectName ? encodeURIComponent(projectName) : '';
  const encodedWelduid = encodeURIComponent(welduid_s_event_torch);
  const encodedViewType = encodeURIComponent(viewType);
  const encodedTorchType = encodeURIComponent(torchType);
  const encodedTiltStart = encodeURIComponent(String(tiltStart));
  const encodedTiltEnd = encodeURIComponent(String(tiltEnd));

  const url =
    `/log?project_id=${encodedProjectId}` +
    (encodedProjectName ? `&project_name=${encodedProjectName}` : '') +
    `&welduid_s_event_torch=${encodedWelduid}` +
    `&view_type=${encodedViewType}` +
    `&torch_type=${encodedTorchType}` +
    `&tilt_start=${encodedTiltStart}` +
    `&tilt_end=${encodedTiltEnd}`;

  const res = await axiosConfig.get(url);
  if (res.status < 200 || res.status >= 300) return [];

  const payload = get(res, ['data'], {});
  const records = extractTlogRecords(payload);
  if (!records.length) return [];

  return records.map((r: Record<string, unknown>) => ({
    tilt: handleNullValue(r.tilt),
    time: handleNullValue(r.time ?? r.s_time),
    volts: handleNullValue(r.volts ?? r.volts_avg ?? r.averageVoltage),
    current: handleNullValue(r.current ?? r.current_avg ?? r.averageCurrent),
    travelSpeed: handleNullValue(r.travel_speed ?? r.travel_speed_avg ?? r.averageTravelSpeed),
    heat: handleNullValue(r.heat ?? r.heat_avg ?? r.arcEnergy ?? r.averageArcEnergy),
  }));
}

function extractTlogRecords(payload: unknown): Record<string, unknown>[] {
  if (Array.isArray(payload)) return payload as Record<string, unknown>[];
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as Record<string, unknown>[];
    if (Array.isArray(obj.records)) return obj.records as Record<string, unknown>[];
    if (obj.data && typeof obj.data === 'object') {
      const nested = obj.data as Record<string, unknown>;
      if (Array.isArray(nested.data)) return nested.data as Record<string, unknown>[];
      if (Array.isArray(nested.records)) return nested.records as Record<string, unknown>[];
    }
  }
  return [];
}

export function collectViolationsForWeld(
  passRecords: PassViewRecord[],
  zoneRecords: ZoneViewRecord[],
  tiltRecords: TiltViewRecord[],
  limits: LimitsRow[],
): WpsViolationRecord[] {
  const passViolations = passRecords.flatMap((p) =>
    violationsFromViewRecord(
      {
        params: p.params,
        jobNumber: p.jobNumber,
        passName: p.name,
        welderId: p.welderId,
      },
      limits,
      'pass',
    ),
  );

  const zoneViolations = zoneRecords.flatMap((z) =>
    violationsFromViewRecord(
      {
        params: z.params,
        jobNumber: z.jobNumber,
        passName: z.pass,
        zoneName: z.zone,
        welderId: z.welderId,
      },
      limits,
      'zone',
    ),
  );
  const tiltViolations = tiltRecords.flatMap((t) =>
    violationsFromViewRecord(
      {
        params: t.params,
        jobNumber: t.jobNumber,
        passName: t.pass,
        tiltStart: t.tiltStart,
        tiltEnd: t.tiltEnd,
        welderId: t.welderId,
      },
      limits,
      'tilt',
    ),
  );

  return [...passViolations, ...zoneViolations, ...tiltViolations];
}
